import { TUNABLES } from "../config";
import type { FactionId } from "../core";
import { DUEL_TABLE, FACTIONS, type RoomMode, seatTable, type TableSeat } from "./protocol";

/** A room's sides (K6.1): each side's kingdoms, as `rules.sides` lists them. */
export type RoomSides = readonly (readonly FactionId[])[];

const ALLIES_SIDES: RoomSides = TUNABLES.alliesMode.sides;
const WAR_SIDES: RoomSides = TUNABLES.warMode.sides;

/**
 * A room's table by its mode (K2, ADR-0017 point 1), laid out as `createRun` lays out the mode's
 * run (`seatPlaces`) but without the core, which the server never runs: the duel's two Kings, the
 * company's King and `companyMode.seats.perKingdom` soldier seats in each of its two kingdoms,
 * two against two's four Kings (K6.1) and the war's twenty (K7.1), their kingdoms in the run's
 * order of `FACTION_IDS`.
 * `test/seatTable.test.ts` proves each equal to the table of the run the page builds.
 */
export const ROOM_TABLES: { readonly [M in RoomMode]: readonly TableSeat[] } = {
  duel: DUEL_TABLE,
  company: seatTable(
    ["player", "enemy"],
    [TUNABLES.companyMode.seats.perKingdom, TUNABLES.companyMode.seats.perKingdom],
  ),
  allies: seatTable(["player", "enemy", "seat3", "seat4"]),
  war: seatTable(FACTIONS.slice(0, WAR_SIDES.flat().length)),
};

/**
 * The sides a room seats by (K6.1): two against two's and the war's (K7.1), the run's
 * `rules.sides`; none for the duel and the company, where every kingdom is its own side.
 */
export const ROOM_SIDES: { readonly [M in RoomMode]: RoomSides | undefined } = {
  duel: undefined,
  company: undefined,
  allies: ALLIES_SIDES,
  war: WAR_SIDES,
};

/** How many seats a game of the mode has: the count on its card. */
export function seatsOf(mode: RoomMode): number {
  return ROOM_TABLES[mode].length;
}
